import type {ReactElement} from 'react';
import {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import Divider from '@Components/divider';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {noop} from '@Utils/general.util';
import styles from './color-palettes-container.module.scss';
import {ColorPalette} from '@Components/color-palettes/color-palette/color-palette';
import {ColorPaletteSize} from '@Components/color-palettes/color-palette/color-palette.types';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {isSameColorPalette} from '@Libraries/color-palettes.library';
import {useAppSelector} from '@/hooks';
import {getUniqueString} from '@Utils/string.util';

export interface ColorPalettesContianerProps {
  heading: string | null;
  colorPalettes: ColorPaletteInfo[];
  onColorPaletteDoneApplying: () => void;
  showPaletteName?: boolean;
  showNameOverlayOnHover?: boolean;
  maximumVisiblePalettes?: number;
  onColorPaletteSelected?: (colorPalette: ColorPaletteInfo, addToRecents?: boolean) => void;
  allowShuffle?: boolean;
  showApplyingState?: boolean;
  colorPaletteAppliedEvent: string;
  useUniqueStringAsKeyForPalette?: boolean;
}

export function ColorPalettesContainer({
  heading,
  colorPalettes,
  showPaletteName = false,
  showNameOverlayOnHover = false,
  maximumVisiblePalettes = Infinity,
  onColorPaletteSelected = noop,
  allowShuffle = false,
  showApplyingState = true,
  useUniqueStringAsKeyForPalette = false,
  ...props
}: ColorPalettesContianerProps): ReactElement<any> | null {
  const selectedColorPalette = useAppSelector((state) => {
    return state.colorPalettes.selectedColorPalette ?? null;
  });

  const colorPaletteBeingApplied = useAppSelector((state) => {
    return state.colorPalettes.colorPaletteBeingApplied ?? null;
  });

  const [allPalettesShown, setAllPalettesShown] = useState<boolean>(false);

  const getNumberOfPalettesToShow = (): number => {
    if (allPalettesShown) {
      return colorPalettes.length;
    }

    return colorPalettes.length < maximumVisiblePalettes ? colorPalettes.length : maximumVisiblePalettes;
  };

  const getColorPalettesToShow = (): ReactElement<any>[] => {
    const palettes = [];
    const numberOfPalettesToShow = getNumberOfPalettesToShow();

    for (let i = 0; i < numberOfPalettesToShow; i++) {
      const colorPalette = colorPalettes[i];

      palettes.push(
        <ColorPalette
          onColorPaletteDoneApplying={props.onColorPaletteDoneApplying}
          colorPaletteAppliedEvent={props.colorPaletteAppliedEvent}
          allowShuffle={allowShuffle}
          showApplyingState={showApplyingState}
          key={useUniqueStringAsKeyForPalette ? getUniqueString() : `${colorPalette.type}-${colorPalette.id}`}
          palette={colorPalette}
          size={ColorPaletteSize.MEDIUM}
          showNameOverlayOnHover={showNameOverlayOnHover}
          showNameLabel={showPaletteName}
          isSelected={isSameColorPalette(colorPalette, selectedColorPalette)}
          isBeingApplied={isSameColorPalette(colorPalette, colorPaletteBeingApplied)}
          disableSelection={colorPaletteBeingApplied !== null && !isSameColorPalette(colorPalette, colorPaletteBeingApplied)}
          onClick={() => {
            if (isSameColorPalette(selectedColorPalette, colorPalette)) {
              onColorPaletteSelected(colorPalette, false);
            } else {
              onColorPaletteSelected(colorPalette);
            }
          }}
        />
      );
    }

    if (numberOfPalettesToShow < colorPalettes.length) {
      palettes.push(getViewAllButton());
    } else if (maximumVisiblePalettes < colorPalettes.length) {
      palettes.push(getViewLessButton());
    }

    return palettes;
  };

  const getViewAllButton = (): ReactElement<any> => {
    return (
      <Button
        key={'view_all_btn'}
        customClasses={styles.viewSwitchButton}
        size={ButtonSize.SMALL}
        type={ButtonType.TERTIARY}
        text={window.i18next.t('pmwjs_view_all')}
        icon="icon-caret-down"
        iconPlacement={IconPlacement.RIGHT}
        onClick={(): void => {
          setAllPalettesShown(true);
        }}
      />
    );
  };

  const getViewLessButton = (): ReactElement<any> => {
    return (
      <Button
        key={'view_less_btn'}
        customClasses={styles.viewSwitchButton}
        size={ButtonSize.SMALL}
        type={ButtonType.TERTIARY}
        text={window.i18next.t('pmwjs_view_less')}
        icon="icon-caret-up"
        iconPlacement={IconPlacement.RIGHT}
        onClick={(): void => {
          setAllPalettesShown(false);
        }}
      />
    );
  };

  const getHeadingComponent = (): ReactElement<any> | null => {
    if (!heading) {
      return null;
    }

    return (
      <div className="spacing-p-b-4">
        <Text val={heading} size={TextSize.XSMALL} bold />
        {getDividerComponent()}
      </div>
    );
  };

  const getDividerComponent = (): ReactElement<any> | null => {
    if (colorPalettes.length === 0) {
      return null;
    }

    return <Divider additionalClasses="spacing-m-t-1" />;
  };

  const getContainerClasses = (): string => {
    return `${styles.paletteContainer} ${!showPaletteName ? styles.wide : ''}`;
  };

  return getNumberOfPalettesToShow() > 0 ? (
    <div>
      {getHeadingComponent()}
      <div className={getContainerClasses()}>{getColorPalettesToShow()}</div>
    </div>
  ) : null;
}
